import { Component, ElementRef, EventEmitter, inject, Input, NgZone, OnChanges, OnDestroy, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { AppModule } from '../../app.module';
import { ChatMessage, ChatSenderType, chatSenderNames, PendingChatMessage, ChatEscalation } from '../../models/chat';
import { ChatService } from '../chat.service';
import { ChatAlertsService } from '../chat-alerts.service';
import { ChatPushService } from '../chat-push.service';
import { UserType } from '../enums';
import { mediaInfo, MediaKind } from '../media';
import { provideNzIconsPatch } from 'ng-zorro-antd/icon';
import { AudioOutline, BellOutline, CameraOutline, CloseOutline, DeleteOutline, DownOutline, ExclamationCircleOutline, HistoryOutline, MessageOutline,
  PaperClipOutline, ReloadOutline, SelectOutline, SendOutline, ShareAltOutline, StopOutline,
  VideoCameraOutline, WhatsAppOutline } from '@ant-design/icons-angular/icons';

interface AttachmentDraft { file: File; url: string; kind: MediaKind; durationSeconds?: number; }

@Component({
  selector: 'app-incident-chat',
  imports: [AppModule],
  providers: [ChatPushService, provideNzIconsPatch([AudioOutline, BellOutline, CameraOutline, CloseOutline, DeleteOutline, ExclamationCircleOutline, HistoryOutline,
    DownOutline, MessageOutline, PaperClipOutline, ReloadOutline, SelectOutline, SendOutline, ShareAltOutline,
    StopOutline, VideoCameraOutline, WhatsAppOutline])],
  templateUrl: './incident-chat.html',
  styleUrl: './incident-chat.less'
})
export class IncidentChat implements OnChanges, OnDestroy {
  @Input({ required: true }) incidentId = 0;
  @Input({ required: true }) senderType: ChatSenderType = ChatSenderType.Callcentre;
  @Input() incidentNumber = '';
  @Input() refreshKey: unknown;
  @Input() notificationsEnabled = false;
  @Input() chatEscalations: ChatEscalation[] = [];
  @Output() fleetChatUnavailable = new EventEmitter<void>();
  @ViewChild('messageList') messageList?: ElementRef<HTMLElement>;
  @ViewChild('messageInput') messageInput?: ElementRef<HTMLTextAreaElement>;
  readonly push = inject(ChatPushService);
  private route = inject(ActivatedRoute, { optional: true });
  private router = inject(Router, { optional: true });
  private notificationLinkOpened = false;

  readonly senderNames = chatSenderNames;
  readonly mediaInfo = mediaInfo;
  visible = false;
  composerExpanded = false;
  loading = false;
  loadingOlder = false;
  hasMore = false;
  messages: ChatMessage[] = [];
  pending: PendingChatMessage[] = [];
  progress: Record<string, number | undefined> = {};
  text = '';
  attachments: AttachmentDraft[] = [];
  preparing = false;
  saving = false;
  error = '';
  historyNotice = '';
  recording = false;
  requestingMicrophone = false;
  recordingSeconds = 0;
  selecting = false;
  selectedIds = new Set<number>();
  forwarding = false;
  escalatingId: number | null = null;
  highlightedMessageId = 0;
  preparingShare = false;
  sharing = false;
  forwardNotice = '';
  forwardError = '';
  private shareData?: ShareData;
  private forwardVersion = 0;
  private recorder?: MediaRecorder;
  private stream?: MediaStream;
  private recordingTimer?: ReturnType<typeof setInterval>;
  private recordingStop?: ReturnType<typeof setTimeout>;
  private discardRecording = false;
  private generation = 0;
  private captureGenerations = new WeakMap<HTMLInputElement, number>();
  private refreshVersion = 0;
  private loadAllHistory = false;
  private pendingVersion = 0;
  private destroyed = false;
  private subscription: Subscription;
  private backgrounded = document.hidden;
  private resumeMessageIds: Set<number> | undefined = document.hidden ? new Set() : undefined;
  private online = () => {
    void this.processPending();
    if (this.visible || (this.mobile && this.resumeMessageIds)) void this.refresh(true, !!this.resumeMessageIds);
  };
  private background = () => {
    if (!this.mobile) return;
    if (!this.backgrounded) this.resumeMessageIds = new Set(this.messages.map(m => m.chatMessageId));
    this.backgrounded = true;
    this.chatAlerts?.setForeground(false);
  };
  private foreground = () => {
    if (!this.mobile || this.destroyed || document.hidden || !this.backgrounded) return;
    this.backgrounded = false;
    this.chatAlerts?.setForeground(true);
    if (this.incidentId > 0) {
      void this.processPending();
      void this.refresh(true, true);
    }
  };
  private visibilityChanged = () => {
    if (document.hidden) {
      if (this.recording) this.stopRecording();
      this.background();
    } else this.foreground();
  };

  get mobile() { return this.senderType !== ChatSenderType.Callcentre; }
  get isFleet() { return this.senderType === ChatSenderType.Fleet; }
  get sessionKey() { return `incidentChat:${this.senderType}:${this.incidentId}`; }
  get unreadCount() { return this.chat.unreadCount(this.incidentId, this.senderType); }
  get hasEscalation() { return !this.mobile && this.chat.hasEscalation(this.incidentId); }
  get readStateNotice() { return this.chat.readStateNotice; }
  get composerOpen() { return this.composerExpanded || this.recording || this.requestingMicrophone; }
  get composerId() { return `incident-chat-composer-${this.senderType}-${this.incidentId}`; }
  private get readingChat() { return this.visible && (!this.mobile || (!this.backgrounded && !document.hidden)); }
  get selectedMessages() { return this.messages.filter(m => this.selectedIds.has(m.chatMessageId)).sort((a, b) => a.chatMessageId - b.chatMessageId); }
  get forwardBusy() { return this.forwarding || this.preparingShare || this.sharing; }
  get shareReady() { return !!this.shareData; }
  get shareSupported() {
    return typeof navigator.share === 'function' &&
      (!this.selectedMessages.some(m => m.document) || typeof navigator.canShare === 'function');
  }

  constructor(private chat: ChatService, private zone: NgZone, @Optional() private chatAlerts: ChatAlertsService | null) {
    this.chatAlerts?.setForeground(!this.backgrounded);
    this.subscription = chat.updates.subscribe(update => {
      if (update.incidentId !== this.incidentId || update.chatSenderTypeId !== this.senderType) return;
      if (update.fleetChatUnavailable) { this.disableFleetChat(); return; }
      if (update.clientMessageId && update.progress !== undefined) {
        this.progress[update.clientMessageId] = update.progress;
        return;
      }
      if (update.savedMessage) {
        this.messages = this.merge(this.messages, [update.savedMessage]);
        if (this.readingChat) this.chat.markViewed(this.incidentId, this.senderType, [update.savedMessage]);
        else this.chat.trackHistory(this.incidentId, this.senderType, [update.savedMessage]);
        this.scrollToBottom();
      }
      void this.loadPending();
    });
    this.subscription.add(chat.messageCreated.subscribe(update => {
      if (update.incidentId !== this.incidentId ||
          (update.viewerType !== undefined && update.viewerType !== this.senderType)) return;
      if (this.visible) void this.refresh(true);
    }));
    this.subscription.add(chat.socketOpened.subscribe(() => {
      if (!this.mobile && this.visible && this.incidentId > 0) void this.refresh(true);
    }));
    if (this.route?.queryParamMap) this.subscription.add(this.route.queryParamMap.subscribe(params => {
      if (params.get('openChat') !== 'true') this.notificationLinkOpened = false;
      else this.openNotificationLink();
    }));
    window.addEventListener('online', this.online);
    document.addEventListener('visibilitychange', this.visibilityChanged);
    window.addEventListener('blur', this.background);
    window.addEventListener('focus', this.foreground);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['incidentId'] || changes['senderType']) {
      this.generation++;
      this.notificationLinkOpened = false;
      this.escalatingId = null;
      this.highlightedMessageId = 0;
      this.resumeMessageIds = this.backgrounded ? new Set() : undefined;
      this.resetSelection();
      this.stopRecording(true);
      this.clearAttachments();
      this.messages = [];
      this.pending = [];
      this.progress = {};
      this.error = '';
      this.hasMore = false;
      this.loadAllHistory = false;
      this.preparing = this.saving = this.requestingMicrophone = false;
      this.text = '';
      this.visible = false;
      this.composerExpanded = false;
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.sessionKey) || 'null');
        this.visible = !!saved?.open;
        this.text = saved?.text || '';
        if (sessionStorage.getItem(this.sessionKey + ':camera')) {
          this.error = 'Camera session restored. Please capture again if your attachment is not shown.';
          sessionStorage.removeItem(this.sessionKey + ':camera');
        }
      } catch { /* Session restoration is optional. The outbox uses IndexedDB. */ }
    }
    const mobileIncidentLoaded = this.mobile && this.notificationsEnabled && this.incidentId > 0;
    this.push.setIncident(mobileIncidentLoaded ? this.incidentId : 0,
      this.isFleet ? UserType.operator : UserType.serviceProviderOperator);
    if (!this.mobile) this.chat.trackEscalations(this.chatEscalations);
    const openedFromLink = this.openNotificationLink();
    if (this.incidentId > 0) {
      if (!openedFromLink) void this.refresh(false, true);
      void this.loadPending();
      void this.processPending();
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.generation++;
    this.stopRecording(true);
    this.clearAttachments();
    this.subscription.unsubscribe();
    if (this.isFleet) this.chat.setFleetAccess(this.incidentId, false);
    window.removeEventListener('online', this.online);
    document.removeEventListener('visibilitychange', this.visibilityChanged);
    window.removeEventListener('blur', this.background);
    window.removeEventListener('focus', this.foreground);
  }

  remember() {
    try { sessionStorage.setItem(this.sessionKey, JSON.stringify({ open: this.visible, text: this.text })); }
    catch { /* Do not block sending when sessionStorage is unavailable. */ }
  }

  private openNotificationLink(): boolean {
    if (this.incidentId <= 0 || (this.mobile && !this.notificationsEnabled) || this.notificationLinkOpened ||
        this.route?.snapshot.paramMap.get('incidentId') !== String(this.incidentId) ||
        this.route.snapshot.queryParamMap.get('openChat') !== 'true') return false;
    this.notificationLinkOpened = true;
    this.highlightedMessageId = Number(this.route.snapshot.queryParamMap.get('chatMessageId')) || 0;
    this.open();
    // Only a clicked notification link requests opening; later refreshes do not acknowledge.
    void this.router?.navigate([], { relativeTo: this.route, queryParams: { openChat: null, chatMessageId: null },
      queryParamsHandling: 'merge', replaceUrl: true });
    return true;
  }

  async escalate(message: ChatMessage) {
    if (this.senderType !== ChatSenderType.ServiceProvider || message.chatSenderTypeId !== ChatSenderType.ServiceProvider ||
        message.escalatedDate || this.escalatingId !== null) return;
    const id = this.incidentId, generation = this.generation;
    this.escalatingId = message.chatMessageId;
    this.error = '';
    try {
      const saved = await firstValueFrom(this.chat.escalate(id, message.chatMessageId));
      if (generation !== this.generation || this.destroyed) return;
      this.messages = this.merge(this.messages, [saved]);
      if (saved.notificationError) this.error = saved.notificationError;
    } catch (error: any) {
      if (generation === this.generation && !this.destroyed)
        this.error = error?.error?.detail || 'Could not escalate the message. Please try again.';
    } finally {
      if (generation === this.generation) this.escalatingId = null;
    }
  }

  open() {
    this.visible = true;
    this.composerExpanded = false;
    if (this.readingChat) this.chat.markViewed(this.incidentId, this.senderType, this.messages);
    this.remember();
    void this.refresh(true, true, !this.mobile);
    this.scrollToBottom();
  }
  close() { this.visible = false; this.composerExpanded = false; this.resetSelection(); this.stopRecording(true); this.remember(); }

  toggleComposer() {
    if (this.recording || this.requestingMicrophone) return;
    this.composerExpanded = !this.composerExpanded;
    if (!this.composerExpanded) this.messageInput?.nativeElement.blur();
  }

  resetSelection() {
    this.forwardVersion++;
    this.selecting = false;
    this.selectedIds = new Set();
    this.shareData = undefined;
    this.forwarding = this.preparingShare = this.sharing = false;
    this.forwardNotice = this.forwardError = '';
  }

  toggleSelection(message: ChatMessage, checked: boolean) {
    if (this.mobile || this.forwardBusy || !this.messages.some(m => m.chatMessageId === message.chatMessageId)) return;
    if (checked) this.selectedIds.add(message.chatMessageId);
    else this.selectedIds.delete(message.chatMessageId);
    this.shareData = undefined;
    this.forwardVersion++;
    this.forwardNotice = this.forwardError = '';
  }

  async sendSelectedToSelf() {
    if (this.mobile || this.forwardBusy || !this.selectedMessages.length) return;
    const id = this.incidentId, version = ++this.forwardVersion;
    const ids = this.selectedMessages.map(m => m.chatMessageId);
    this.forwarding = true;
    this.shareData = undefined;
    this.forwardNotice = this.forwardError = '';
    try {
      const results = await firstValueFrom(this.chat.sendToSelf(id, ids));
      if (this.destroyed || id !== this.incidentId || version !== this.forwardVersion) return;
      let count = 0;
      const errors: string[] = [];
      for (const messageId of ids) {
        const result = results.find(r => r.chatMessageId === messageId);
        if (result?.queuedMessages) { this.selectedIds.delete(messageId); count++; }
        else errors.push(result?.error || 'Could not confirm queuing. Check WhatsApp before retrying.');
      }
      if (count) this.forwardNotice = `Queued for your WhatsApp: ${count} selected message${count === 1 ? '' : 's'}.`;
      if (errors.length) this.forwardError = `${errors.length} not queued. ${[...new Set(errors)].join(' ')}`;
    } catch (error: any) {
      if (id === this.incidentId && version === this.forwardVersion && !this.destroyed)
        this.forwardError = error?.error?.detail || 'Could not confirm queuing. Check WhatsApp before retrying.';
    } finally {
      if (id === this.incidentId && version === this.forwardVersion && !this.destroyed) this.forwarding = false;
    }
  }

  async shareSelected() {
    if (this.mobile || this.forwardBusy || !this.selectedMessages.length || !this.shareSupported) return;
    const id = this.incidentId, version = ++this.forwardVersion;
    const messages = this.selectedMessages;
    this.forwardNotice = this.forwardError = '';
    if (!this.shareData && messages.some(m => m.document)) {
      this.preparingShare = true;
      try {
        const files: File[] = [];
        for (const message of messages) {
          if (!message.document) continue;
          const blob = await firstValueFrom(this.chat.attachment(id, message.chatMessageId));
          if (this.destroyed || id !== this.incidentId || version !== this.forwardVersion) return;
          const document = message.document;
          const name = (document.description || decodeURIComponent(document.url.split('?')[0].split('/').pop() || 'Attachment')).replace(/\\/g, '/').split('/').pop() || 'Attachment';
          files.push(new File([blob], name, { type: mediaInfo(document).mimeType.split(';')[0] }));
        }
        const data = this.shareContent(messages, files);
        if (!navigator.canShare(data)) throw new Error('This browser cannot share these files. Use Send to my WhatsApp.');
        this.shareData = data;
        this.forwardNotice = 'Files ready. Click Share now to choose an app.';
      } catch (error: any) {
        if (id === this.incidentId && version === this.forwardVersion && !this.destroyed)
          this.forwardError = error?.error?.detail || error?.message || 'Could not prepare these files for sharing.';
      } finally {
        if (id === this.incidentId && version === this.forwardVersion && !this.destroyed) this.preparingShare = false;
      }
      return;
    }
    this.sharing = true;
    try {
      // This call runs directly from the click, preserving the browser's user activation.
      await navigator.share(this.shareData || this.shareContent(messages));
      if (id === this.incidentId && version === this.forwardVersion && !this.destroyed) this.resetSelection();
    } catch (error: any) {
      if (id === this.incidentId && version === this.forwardVersion && !this.destroyed && error?.name !== 'AbortError')
        this.forwardError = 'Could not open system share for these messages. Use Send to my WhatsApp.';
    } finally {
      if (id === this.incidentId && version === this.forwardVersion && !this.destroyed) this.sharing = false;
    }
  }

  private shareContent(messages: ChatMessage[], files: File[] = []): ShareData {
    const text = messages.map(m => m.message).filter(Boolean).join('\n\n');
    return { ...(text ? { text } : {}), ...(files.length ? { files } : {}) };
  }

  capture(input: HTMLInputElement) {
    this.captureGenerations.set(input, this.generation);
    this.remember();
    try { sessionStorage.setItem(this.sessionKey + ':camera', 'true'); } catch { /* Optional recovery hint. */ }
    input.click();
  }

  private disableFleetChat() {
    this.chat.setFleetAccess(this.incidentId, false);
    this.refreshVersion++;
    this.pendingVersion++;
    this.generation++;
    this.visible = this.loading = false;
    this.messages = [];
    this.pending = [];
    this.stopRecording(true);
    this.clearAttachments();
    this.remember();
    this.fleetChatUnavailable.emit();
  }

  async refresh(preserveHistory = false, loadAll = false, acknowledgeOnOpen = false) {
    const id = this.incidentId;
    const version = ++this.refreshVersion;
    // A live update may supersede the full-history request started by opening chat.
    this.loadAllHistory ||= loadAll;
    const fetchAll = this.loadAllHistory;
    const knownIds = new Set(this.messages.map(m => m.chatMessageId));
    const latestKnownId = this.messages.at(-1)?.chatMessageId;
    this.loading = true;
    this.historyNotice = '';
    const readState = this.chat.loadReadState(id, this.senderType);
    try {
      const history = await firstValueFrom(this.chat.history(id, this.senderType));
      await readState;
      if (this.destroyed || id !== this.incidentId || version !== this.refreshVersion) return;
      // Bridge any gap after a reconnect/burst before merging with previously loaded pages.
      while (history.hasMore && history.messages.length && (fetchAll ||
             (preserveHistory && latestKnownId !== undefined && history.messages[0].chatMessageId > latestKnownId))) {
        const beforeId = history.messages[0].chatMessageId;
        const older = await firstValueFrom(this.chat.history(id, this.senderType, beforeId));
        if (this.destroyed || id !== this.incidentId || version !== this.refreshVersion) return;
        if (!older.messages.length || older.messages[0].chatMessageId >= beforeId) break;
        history.messages = this.merge(older.messages, history.messages);
        history.hasMore = older.hasMore;
      }
      const list = this.messageList?.nativeElement;
      const scroll = !preserveHistory || !list || list.scrollHeight - list.scrollTop - list.clientHeight < 50;
      if (!preserveHistory || !this.messages.length ||
          (history.messages.length && history.messages[0].chatMessageId <= this.messages[0].chatMessageId)) {
        this.hasMore = history.hasMore;
      }
      // Manual/mobile refresh replaces the tail; live updates retain already loaded history.
      this.messages = this.merge(this.messages.filter(m => preserveHistory || !knownIds.has(m.chatMessageId)), history.messages);
      if (!this.mobile) this.chat.trackChatEscalations(history.messages);
      this.loadAllHistory = false;
      const resumed = this.mobile && !this.backgrounded && !document.hidden && this.resumeMessageIds !== undefined;
      const viewedIds = new Set(resumed ? this.chat.viewedMessages.filter(m => m.incidentId === id && m.viewerType === this.senderType).map(m => m.chatMessageId) : []);
      const missed = resumed ? this.messages.filter(m => !this.resumeMessageIds!.has(m.chatMessageId) &&
        !viewedIds.has(m.chatMessageId) && m.chatSenderTypeId !== this.senderType) : [];
      if (this.readingChat) this.chat.markViewed(id, this.senderType, this.messages);
      else this.chat.trackHistory(id, this.senderType, this.messages);
      if (resumed) {
        this.resumeMessageIds = undefined;
        if (missed.length) this.chatAlerts?.notifyMany(missed.map(m => ({ incidentId: id, chatMessageId: m.chatMessageId })));
      }
      try { await this.chat.cache(id, { messages: this.messages, hasMore: this.hasMore }); } catch { this.historyNotice = 'History could not be saved for offline viewing.'; }
      if (this.destroyed || id !== this.incidentId || version !== this.refreshVersion) return;
      const escalated = history.messages.filter(m => m.escalatedDate && !m.escalationAcknowledgedDate);
      if (acknowledgeOnOpen && !this.mobile && this.visible && !document.hidden && escalated.length) {
        try {
          const acknowledged = await firstValueFrom(this.chat.acknowledgeEscalations(id, escalated));
          if (this.destroyed || id !== this.incidentId || version !== this.refreshVersion) return;
          this.messages = this.merge(this.messages, acknowledged);
        } catch {
          if (id === this.incidentId && version === this.refreshVersion)
            this.historyNotice = 'Chat loaded, but escalation acknowledgement failed. Close and open chat to retry.';
        }
      }
      if (scroll) this.scrollToBottom();
    } catch (error: any) {
      await readState;
      if (this.destroyed || id !== this.incidentId || version !== this.refreshVersion) return;
      if (this.isFleet && error?.status === 403) { this.disableFleetChat(); return; }
      this.historyNotice = error?.status === 404 ? 'Incident not found.' : 'Could not refresh. Showing saved messages; please try Refresh again.';
      try {
        const cached = await this.chat.cached(id);
        if (cached && id === this.incidentId && version === this.refreshVersion && !this.destroyed) {
          this.messages = this.merge(cached.messages, this.messages);
          this.hasMore = cached.hasMore;
          if (this.readingChat) this.chat.markViewed(id, this.senderType, this.messages);
          else this.chat.trackHistory(id, this.senderType, this.messages);
        }
      } catch { /* History fetching must work even if local storage is unavailable. */ }
    } finally {
      if (id === this.incidentId && version === this.refreshVersion && !this.destroyed) this.loading = false;
    }
  }

  async older() {
    if (this.loadingOlder || !this.messages.length) return;
    const id = this.incidentId;
    const version = this.refreshVersion;
    this.loadingOlder = true;
    const list = this.messageList?.nativeElement;
    const height = list?.scrollHeight || 0;
    try {
      const history = await firstValueFrom(this.chat.history(id, this.senderType, this.messages[0].chatMessageId));
      if (id !== this.incidentId || version !== this.refreshVersion || this.destroyed) return;
      this.messages = this.merge(history.messages, this.messages);
      this.hasMore = history.hasMore;
      if (this.readingChat) this.chat.markViewed(id, this.senderType, this.messages);
      else this.chat.trackHistory(id, this.senderType, this.messages);
      setTimeout(() => { if (list) list.scrollTop += list.scrollHeight - height; });
    } catch (error: any) {
      if (id === this.incidentId && version === this.refreshVersion && !this.destroyed) {
        if (this.isFleet && error?.status === 403) this.disableFleetChat();
        else this.historyNotice = 'Could not load older messages. Please try again.';
      }
    }
    finally { this.loadingOlder = false; }
  }

  private merge(a: ChatMessage[], b: ChatMessage[]) {
    return [...new Map([...a, ...b].map(m => [m.chatMessageId, m])).values()]
      .sort((x, y) => x.chatMessageId - y.chatMessageId);
  }

  private async loadPending() {
    const generation = this.generation;
    const version = ++this.pendingVersion;
    try {
      const pending = await this.chat.pending(this.incidentId, this.senderType);
      if (generation === this.generation && version === this.pendingVersion && !this.destroyed) this.pending = pending;
    } catch { if (!this.destroyed) this.error = 'Local storage is unavailable. Messages cannot be queued safely.'; }
  }

  private async processPending() {
    if (this.incidentId <= 0) return;
    try { await this.chat.process(this.incidentId, this.senderType); }
    catch { if (!this.destroyed) this.error = 'Could not process queued messages. Please use Retry.'; }
  }

  async retry(item: PendingChatMessage) {
    this.error = '';
    try { await this.chat.retry(item); }
    catch { this.error = 'Could not retry the message. Please try again.'; }
  }

  async removePending(item: PendingChatMessage) {
    if (item.status === 'sending') return;
    try { await this.chat.remove(item); }
    catch { this.error = 'Could not remove the pending message.'; }
  }

  async selectFiles(event: Event, sendCaptured = false) {
    try { sessionStorage.removeItem(this.sessionKey + ':camera'); } catch { /* Optional recovery hint. */ }
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';
    const autoSend = sendCaptured && this.mobile;
    const generation = autoSend ? this.captureGenerations.get(input) ?? this.generation : this.generation;
    this.captureGenerations.delete(input);
    if (generation !== this.generation || this.destroyed || (autoSend && !this.visible)) return;
    this.error = '';
    try {
      for (const file of files) {
        if (generation !== this.generation || this.destroyed) break;
        this.preparing = true;
        const attachment = await this.addAttachment(file, generation);
        if (generation !== this.generation || this.destroyed) break;
        if (autoSend && attachment) {
          this.preparing = false;
          if (!this.visible) {
            const index = this.attachments.indexOf(attachment);
            if (index >= 0) this.removeAttachment(index);
            break;
          }
          await this.send(attachment);
        }
      }
    } catch (error: any) { if (generation === this.generation) this.error = error.message || 'Could not read the attachment.'; }
    finally { if (generation === this.generation) this.preparing = false; }
  }

  private async addAttachment(original: File, generation: number, recordedDuration?: number) {
    const info = mediaInfo({ url: original.name, mimeType: original.type });
    if (!['image', 'audio', 'video'].includes(info.kind) || !original.size) {
      throw new Error('Choose a non-empty image, audio or video file.');
    }
    const file = original.type === info.mimeType ? original : new File([original], original.name, { type: info.mimeType });
    const url = URL.createObjectURL(file);
    try {
      let durationSeconds = recordedDuration;
      if (this.mobile && (info.kind === 'audio' || info.kind === 'video')) {
        durationSeconds ??= await this.duration(url, info.kind);
        if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) throw new Error('Could not read the recording duration. Please choose another file.');
        if (durationSeconds > 120) throw new Error('Mobile audio and video must be two minutes or shorter. Please record or select a shorter file.');
      }
      if (generation !== this.generation || this.destroyed) { URL.revokeObjectURL(url); return; }
      const attachment = { file, url, kind: info.kind, durationSeconds };
      this.attachments.push(attachment);
      return attachment;
    } catch (error) { URL.revokeObjectURL(url); throw error; }
  }

  private duration(url: string, kind: string): Promise<number> {
    return new Promise((resolve, reject) => {
      const media = document.createElement(kind === 'audio' ? 'audio' : 'video');
      const finish = (error?: Error) => {
        const value = media.duration;
        clearTimeout(timer);
        media.onloadedmetadata = media.onerror = null;
        media.removeAttribute('src');
        media.load();
        error ? reject(error) : resolve(value);
      };
      const timer = setTimeout(() => finish(new Error('Could not read the recording duration. Please choose another file.')), 15000);
      media.preload = 'metadata';
      media.onloadedmetadata = () => finish();
      media.onerror = () => finish(new Error('This device cannot read the selected audio/video. Please choose another file.'));
      media.src = url;
    });
  }

  removeAttachment(index: number) {
    const [attachment] = this.attachments.splice(index, 1);
    if (attachment) URL.revokeObjectURL(attachment.url);
  }

  private clearAttachments() {
    this.attachments.forEach(a => URL.revokeObjectURL(a.url));
    this.attachments = [];
  }

  async send(capturedAttachment?: AttachmentDraft) {
    if (this.saving || this.preparing || this.recording || this.requestingMicrophone || (!this.text.trim() && !this.attachments.length)) return;
    const generation = this.generation;
    this.saving = true;
    this.error = '';
    const attachments: (AttachmentDraft | undefined)[] = capturedAttachment ? [capturedAttachment] : this.attachments.length ? this.attachments : [undefined];
    const items = attachments.map((attachment, i): PendingChatMessage => ({
      clientMessageId: crypto.randomUUID(), incidentId: this.incidentId, chatSenderTypeId: this.senderType,
      message: i === 0 && !capturedAttachment ? this.text.trim() : '', file: attachment?.file, filename: attachment?.file.name,
      durationSeconds: attachment?.durationSeconds, createdDate: new Date(Date.now() + i).toISOString(), status: 'pending'
    }));
    try {
      await this.chat.enqueue(items);
      if (generation === this.generation && !this.destroyed) {
        if (capturedAttachment) {
          const index = this.attachments.indexOf(capturedAttachment);
          if (index >= 0) this.removeAttachment(index);
        } else {
          this.text = '';
          this.clearAttachments();
        }
        this.remember();
        this.scrollToBottom();
      }
      await this.chat.process(items[0].incidentId, items[0].chatSenderTypeId);
    } catch { if (generation === this.generation) this.error = 'Could not save or send the message. Check the pending list before retrying.'; }
    finally { if (generation === this.generation) this.saving = false; }
  }

  async startRecording() {
    if (this.recording || this.requestingMicrophone || this.preparing || this.saving) return;
    this.composerExpanded = true;
    this.error = '';
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      this.error = 'Voice recording is unavailable. Use a browser that supports microphone recording over HTTPS.';
      return;
    }
    const generation = this.generation;
    this.requestingMicrophone = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (generation !== this.generation || this.destroyed || !this.visible) { stream.getTracks().forEach(t => t.stop()); return; }
      this.stream = stream;
      const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'].find(t => MediaRecorder.isTypeSupported(t));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 32000 } : undefined);
      this.recorder = recorder;
      this.discardRecording = false;
      const chunks: BlobPart[] = [];
      const started = Date.now();
      recorder.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };
      recorder.onstop = () => this.zone.run(() => {
        if (generation !== this.generation || this.destroyed) return;
        const discarded = this.discardRecording;
        this.releaseMicrophone();
        if (discarded) { this.preparing = false; return; }
        const type = recorder.mimeType || 'audio/webm';
        const extension = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
        const file = new File(chunks, `Voice note.${extension}`, { type });
        void this.addAttachment(file, generation, Math.min(120, (Date.now() - started) / 1000))
          .then(attachment => {
            if (generation !== this.generation || this.destroyed) return;
            this.preparing = false;
            if (!attachment) return;
            if (this.discardRecording || !this.visible) {
              const index = this.attachments.indexOf(attachment);
              if (index >= 0) this.removeAttachment(index);
              return;
            }
            void this.send(attachment);
          })
          .catch(error => {
            if (generation === this.generation && !this.destroyed) {
              this.preparing = false;
              this.error = error.message;
            }
          });
      });
      recorder.onerror = () => this.zone.run(() => {
        if (generation !== this.generation || this.destroyed) return;
        this.stopRecording(true);
        this.error = 'Recording failed. Please try again.';
      });
      recorder.start();
      this.recording = true;
      this.recordingSeconds = 0;
      this.recordingTimer = setInterval(() => this.recordingSeconds = Math.min(120, Math.floor((Date.now() - started) / 1000)), 250);
      this.recordingStop = setTimeout(() => this.stopRecording(), 120000);
    } catch {
      this.releaseMicrophone();
      if (generation === this.generation) this.error = 'Could not start recording. Please allow microphone access and try again.';
    } finally { if (generation === this.generation) this.requestingMicrophone = false; }
  }

  stopRecording(discard = false) {
    if (discard) {
      this.discardRecording = true;
      if (this.recorder) {
        this.recorder.onstop = null;
        this.recorder.ondataavailable = null;
        this.recorder.onerror = null;
        if (this.recorder.state !== 'inactive') this.recorder.stop();
        this.preparing = false;
      }
      this.releaseMicrophone();
      return;
    }
    if (!this.recording || !this.recorder) return;
    this.recording = false;
    this.preparing = true;
    clearInterval(this.recordingTimer);
    clearTimeout(this.recordingStop);
    if (this.recorder.state !== 'inactive') this.recorder.stop();
  }

  private releaseMicrophone() {
    clearInterval(this.recordingTimer);
    clearTimeout(this.recordingStop);
    this.stream?.getTracks().forEach(t => t.stop());
    this.stream = undefined;
    this.recorder = undefined;
    this.recording = false;
  }

  private scrollToBottom() {
    setTimeout(() => {
      const list = this.messageList?.nativeElement;
      if (!this.destroyed && list) {
        const highlighted = this.highlightedMessageId > 0
          ? list.querySelector(`[data-chat-message-id="${this.highlightedMessageId}"]`) : null;
        if (highlighted) highlighted.scrollIntoView({ block: 'center' });
        else list.scrollTop = list.scrollHeight;
      }
    });
  }
}
